/**
 * MotionReveals.jsx — Petits utilitaires Framer Motion réutilisables.
 * Toutes les anims respectent prefers-reduced-motion (Framer le fait nativement).
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────
// FadeUp — apparaît du bas vers le haut au scroll
// ─────────────────────────────────────────────────────────────────
export function FadeUp({ children, delay = 0, y = 24, className = '', ...rest }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 1 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Stagger — révèle les enfants en cascade
// Usage: <Stagger><Card /><Card /></Stagger>
// ─────────────────────────────────────────────────────────────────
export function Stagger({ children, className = '', staggerChildren = 0.08, delayChildren = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? 'visible' : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren, delayChildren },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = '', y = 20 }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y },
        visible: {
          opacity: 1, y: 0,
          transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AnimatedCounter — compteur qui monte de 0 → value à la vue
// Gère les suffixes ("100%", "3 200", "9", "31")
// ─────────────────────────────────────────────────────────────────
export function AnimatedCounter({ value, duration = 1.4, className = '', prefix = '', suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();

  // Parse numeric value + optional trailing symbol (% etc.)
  const match = String(value).match(/^([\d\s]+)(.*)$/);
  const target = match ? parseInt(match[1].replace(/\s/g, ''), 10) : 0;
  const tailSym = match ? match[2] : '';

  const [display, setDisplay] = useState(reduce ? target : 0);

  useEffect(() => {
    if (!inView || reduce) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration, reduce]);

  const formatted = target >= 1000 ? display.toLocaleString('fr-FR') : display;

  return (
    <span ref={ref} className={className}>
      {prefix}{formatted}{tailSym}{suffix}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// ScrollScale — petit zoom subtil au scroll (parallax-like)
// Usage: wrappe une <img> qui doit zoomer en entrant à l'écran
// ─────────────────────────────────────────────────────────────────
export function ScrollScale({ children, from = 1.08, to = 1.0, className = '' }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { scale: 1 } : { scale: from }}
      whileInView={{ scale: to }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
